import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLinkClickHandler } from 'react-router-dom';
import { ColumnMappingConfig } from '@asset-metrix/portal-gateway-client-js';
import {
  getActiveContentGroupTabUrlSearchKey,
  isDocumentPreviewEnabled,
  isWorkflowVisualizationData,
  SidebarDocument,
  SidebarDocumentPanel,
} from '@asset-metrix/portal-ui';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  DatatableRelatedDocumentsQueryKey,
  fetchDatatableRelatedDocuments,
} from '../../../api/portalGateway/datatableRelatedDocuments';
import {
  NO_ROLE_ID,
  portalGatewayClient,
} from '../../../api/portalGatewayClient';
import { createTableFilterSearchParamKey } from '../../../hooks/usePresetTableFiltersFromUrl';
import { useStore } from '../../../store';
import { isWidgetTableFilterStoreKey } from '../../../utils/filterInstanceId';
import { prependRoleId } from '../../../utils/prependRoleId';
import { pageWithRoleLink } from '../../../utils/urlPath';
import type { PageSidebarPreviewedDocument } from '../../page-sidebar/types';
import { mapColumnData } from '../mapColumnData';

import { downloadSidebarDocument } from './downloadSidebarDocument';
import {
  deduplicateDocumentsById,
  getFileDownloadColumnDocuments,
  resolveSidebarDocumentSelection,
} from './sidebarDocumentUtils';
import { TableSidebarLoaderProps } from './useTableSidebarProperties';

export type DatatableSidebarLoaderProps = TableSidebarLoaderProps & {
  onSelectedDocumentChange?: (
    document: PageSidebarPreviewedDocument | null
  ) => void;
  widgetId: string;
};

export const DatatableSidebarLoader = ({
  data,
  relatedDocumentsConfig,
  onSelectedDocumentChange,
  widgetId,
  opened,
  showWorkflowFiles,
  fileDownloadColumnKeys,
}: DatatableSidebarLoaderProps) => {
  const { t } = useTranslation();
  const [selectedDocumentPreviewUrl, setSelectedDocumentPreviewUrl] = useState<
    string | undefined
  >(undefined);
  const { roleId, deleteFilterSelections } = useStore(
    useShallow((state) => ({
      roleId: state.activeUserRole?.id,
      deleteFilterSelections: state.deleteFilterSelections,
    }))
  );

  const selection = useMemo(
    () => mapColumnData(relatedDocumentsConfig?.columnMapping, data?.data),
    [relatedDocumentsConfig?.columnMapping, data?.data]
  );

  const linkedPageSearchParams = useMemo(() => {
    const searchParams = new URLSearchParams();
    if (relatedDocumentsConfig?.linkedPage?.filteredWidget) {
      const mappings = relatedDocumentsConfig.columnMapping;
      for (const k in mappings) {
        const { id, key, display } = mappings[k];
        const searchKey = createTableFilterSearchParamKey(
          relatedDocumentsConfig.linkedPage.filteredWidget.id,
          id
        );
        const value = selection[key];
        const mappedValue = mapToFilterValue(display, value);
        if (mappedValue !== undefined) {
          searchParams.set(searchKey, mappedValue);
        }
      }
    }

    if (relatedDocumentsConfig?.linkedPage?.filteredWidget?.tab) {
      const searchKey = getActiveContentGroupTabUrlSearchKey(
        relatedDocumentsConfig?.linkedPage?.filteredWidget?.tab.group
      );
      const tabValue =
        relatedDocumentsConfig?.linkedPage?.filteredWidget?.tab.value;

      searchParams.set(searchKey, tabValue);
    }
    const searchParamsString = searchParams.toString();
    return searchParamsString ? `?${searchParams.toString()}` : '';
  }, [
    relatedDocumentsConfig?.columnMapping,
    relatedDocumentsConfig?.linkedPage?.filteredWidget,
    selection,
  ]);

  const href = useMemo(
    () =>
      pageWithRoleLink(roleId ?? NO_ROLE_ID, {
        slug: relatedDocumentsConfig?.linkedPage?.completeSlug,
        search: linkedPageSearchParams,
        pageType: 'page',
      }),
    [
      roleId,
      relatedDocumentsConfig?.linkedPage?.completeSlug,
      linkedPageSearchParams,
    ]
  );

  const handleClick = useLinkClickHandler<HTMLButtonElement>(href ?? '');
  const onOpenInPage = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Delete any filters that have been set before
      const widgetId = relatedDocumentsConfig?.linkedPage?.filteredWidget?.id;
      if (widgetId) {
        for (const id of Object.keys(
          useStore.getState().filterSelections ?? {}
        )) {
          if (isWidgetTableFilterStoreKey(id, widgetId)) {
            deleteFilterSelections(id);
          }
        }
      }
      handleClick(event);
    },
    [handleClick, relatedDocumentsConfig, deleteFilterSelections]
  );

  const {
    data: relatedDocumentsData,
    isFetching,
    error,
  } = useQuery({
    queryKey: prependRoleId<DatatableRelatedDocumentsQueryKey>([
      'datatableRelatedDocuments',
      widgetId,
      selection,
    ]),
    queryFn: fetchDatatableRelatedDocuments,
    enabled: opened && !!relatedDocumentsConfig,
  });

  const documents = useMemo(() => {
    // related documents from API
    let documents: SidebarDocument[] = relatedDocumentsData?.data.items ?? [];

    // workflow files
    const workflowVisualizationData = data?.data.workflowVisualization;
    if (
      showWorkflowFiles &&
      isWorkflowVisualizationData(workflowVisualizationData)
    ) {
      const workflowFiles = workflowVisualizationData.steps.flatMap(
        (step) => step.files ?? []
      );
      documents = [...documents, ...workflowFiles];
    }

    documents = [
      ...documents,
      ...getFileDownloadColumnDocuments(data?.data, fileDownloadColumnKeys),
    ];

    return deduplicateDocumentsById(documents);
  }, [
    relatedDocumentsData?.data.items,
    data?.data,
    showWorkflowFiles,
    fileDownloadColumnKeys,
  ]);

  const [selectedDocument, setSelectedDocument] = useState<
    SidebarDocument | undefined
  >(() => resolveSidebarDocumentSelection(documents, undefined));

  const moreEntriesCount = useMemo(() => {
    const totalCount = relatedDocumentsData?.data.meta.totalCount;
    const relatedItemsCount = relatedDocumentsData?.data.items.length ?? 0;

    if (totalCount == null) {
      return undefined;
    }

    return Math.max(0, totalCount - relatedItemsCount);
  }, [
    relatedDocumentsData?.data.meta.totalCount,
    relatedDocumentsData?.data.items.length,
  ]);

  const downloadDocument = useCallback(
    async (document: SidebarDocument, inline = true) => {
      return document && 'downloadToken' in document
        ? // Workflow file - use getDocumentDownloadUrl
          await portalGatewayClient.table.getDocumentDownloadUrl(
            document.id,
            widgetId,
            {
              inline,
              token: document.downloadToken,
            }
          )
        : // Regular related document - use getRelatedDocumentDownloadUrl
          await portalGatewayClient.table.getRelatedDocumentDownloadUrl(
            widgetId,
            document.id,
            {
              selection,
            },
            { inline }
          );
    },
    [widgetId, selection]
  );

  const fetchAndSetDocumentPreviewUrl = useCallback(
    async (document: SidebarDocument | undefined) => {
      try {
        setSelectedDocumentPreviewUrl(undefined);

        if (document == null || !isDocumentPreviewEnabled(document)) {
          return;
        }

        const { data } = await downloadDocument(document);

        if (data?.status === 'success' && data.data.url) {
          setSelectedDocument((current) => {
            if (current?.id === document?.id) {
              setSelectedDocumentPreviewUrl(data.data.url);
            }
            return current;
          });
        }
      } catch (e: unknown) {
        console.error(e);
      }
    },
    [downloadDocument]
  );

  useEffect(() => {
    setSelectedDocument(undefined);
    setSelectedDocumentPreviewUrl(undefined);
  }, [data?.rowId, widgetId]);

  useEffect(() => {
    setSelectedDocument((current) =>
      resolveSidebarDocumentSelection(documents, current)
    );
  }, [documents]);

  useEffect(() => {
    if (selectedDocument == null) {
      setSelectedDocumentPreviewUrl(undefined);
      return;
    }

    fetchAndSetDocumentPreviewUrl(selectedDocument);
  }, [fetchAndSetDocumentPreviewUrl, selectedDocument]);

  const previewedDocument = useMemo(
    () =>
      selectedDocument
        ? { id: selectedDocument.id, label: selectedDocument.fileName }
        : null,
    [selectedDocument]
  );

  useLayoutEffect(() => {
    onSelectedDocumentChange?.(previewedDocument);
  }, [onSelectedDocumentChange, previewedDocument]);

  const onDocumentDownload = useCallback(() => {
    downloadSidebarDocument(selectedDocument, {
      isRelatedDocument:
        selectedDocument != null && !('downloadToken' in selectedDocument),
      query: {
        inline: false,
        token:
          selectedDocument != null && 'downloadToken' in selectedDocument
            ? selectedDocument.downloadToken
            : undefined,
      },
      selection,
      widgetId,
    });
  }, [selectedDocument, selection, widgetId]);

  const onDocumentClick = async (document: SidebarDocument) => {
    if (document.id === selectedDocument?.id && selectedDocumentPreviewUrl) {
      return;
    }
    setSelectedDocumentPreviewUrl(undefined);
    setSelectedDocument(document);
  };

  return (
    <SidebarDocumentPanel
      error={error}
      documents={documents}
      loading={isFetching}
      moreEntriesCount={moreEntriesCount}
      onDocumentDownload={onDocumentDownload}
      onOpenInPage={
        relatedDocumentsConfig?.linkedPage ? onOpenInPage : undefined
      }
      openInPageButtonText={
        relatedDocumentsConfig?.linkedPage?.buttonText ??
        t('app.pageSidebar.openInPage')
      }
      selectedDocument={selectedDocument}
      onDocumentClick={onDocumentClick}
      selectedDocumentPreviewUrl={selectedDocumentPreviewUrl}
    />
  );
};

function mapToFilterValue(
  display: ColumnMappingConfig['display'],
  value: string
) {
  switch (display) {
    case 'range-date-picker':
      // Empty strings (or invalid dates in general) cannot be passed into date range filters. We need to omit them completely.
      if (value.length === 0) {
        return undefined;
      }

      return JSON.stringify({
        gte: value.slice(0, 10),
        lte: value.slice(0, 10),
      });
    default:
      return value;
  }
}
